import { useSignal } from '@funtech-int/rival-react';
import React, { useMemo, type FC } from 'react';
import { useAnalytics } from '@funtech-int/yango-shared';
import { useInView } from 'react-intersection-observer';

import { useI18n } from '../../hooks/useI18n';
import { useUserSettings } from '../../hooks/useUserSettings';
import { useUserSettingsQuery } from '../../hooks/useUserSettingsQuery';
import { LanguageOption } from '../../types/watchingSettings';
import { SettingsDropdown } from '../SettingsDropdown/SettingsDropdown';
import { Cell } from '../Cell/Cell';
import { createSettingsLanguageAnalyticsEvents } from '../../lib/createSettingsLanguageAnalyticsEvents';

import styles from './WatchSettings.module.css';

export const WatchSettings: FC = () => {
    const analytics = useAnalytics(createSettingsLanguageAnalyticsEvents);

    const { settings } = useUserSettingsQuery();

    const i18n = useI18n();

    const userSettings = useUserSettings();
    const preferredLanguage = useSignal(userSettings.preferredLanguage);

    const selectedOptionTitle = settings.availableWatchingLanguages?.find(
        (option) => option.code === preferredLanguage
    )?.title;

    const { ref } = useInView({
        onChange(inView: boolean) {
            if (inView) {
                analytics.showed(preferredLanguage ?? undefined);
            }
        },
        triggerOnce: false,
        threshold: 0.5,
    });

    const controller = useMemo(
        () => ({
            onSelectOptionHandle: (option: LanguageOption) => {
                analytics.selected({
                    newValue: option.code,
                    oldValue: preferredLanguage ?? undefined,
                });
            },

            onDropdownButtonOpenHandle: () => {
                analytics.navigated(preferredLanguage ?? undefined);
            },

            onWatchingLanguageChange: (option: LanguageOption) => {
                const oldValue = preferredLanguage ?? undefined;

                userSettings.updatePreferredLanguage(option.code, {
                    onSuccess: (code) => {
                        analytics.changed({
                            newValue: code,
                            oldValue,
                        });
                    },
                    onError: (error) => {
                        analytics.errorRaised(error);
                    },
                });
            },
        }),
        [analytics, preferredLanguage, userSettings]
    );

    return (
        <div className={styles.root}>
            <Cell
                title={i18n('audio_language__title')}
                description={i18n('audio_language__description')}
                setCellRef={ref}
            >
                <SettingsDropdown
                    defaultDropdownButtonTitle={i18n('audio_language__dropdown_title')}
                    selectedOptionTitle={selectedOptionTitle}
                    options={settings.availableWatchingLanguages}
                    onChangeOption={controller.onWatchingLanguageChange}
                    onDropdownButtonOpen={controller.onDropdownButtonOpenHandle}
                    onSelectOption={controller.onSelectOptionHandle}
                />
            </Cell>
        </div>
    );
};
